import { TApplicationData } from "../components/ApplicationCta/ApplicationSubmit/useApplicationSubmit";
import { AcquisitionFlow, getAcquisitionFlow, getMarketingAttributesForAcquisitions, getProgramData, TAcquisitionsMarketingAttributeArray } from "./acquisition-utils";
import { TSubmitDataPHA, TSubmitDataPqSkipPqPqOfOne, TSubmitDataPreApproved } from "./acquisition-utils-buildAppData.types";
import { blackboxUtils } from "./blackbox-utils";
import SessionStorage from "./SessionStorage";

export const buildSubmitDataForPqSkipPqPqOfOne = async (dataToSubmit: TApplicationData): Promise<TSubmitDataPqSkipPqPqOfOne> => {

    // Any used as the type to deal with nested object types below. 
    const sessionStoragePersonalInfo = (await SessionStorage.tryGetCipher<any>('personalInfo', {}));

    const blackBoxData = blackboxUtils.getBlackbox();

    const personalInformationObject = AcquisitionFlow.SkipPQ !== getAcquisitionFlow() 
        ? {
            ...sessionStoragePersonalInfo,
            PrimaryPhoneNumber: dataToSubmit["PersonalInformation-PrimaryPhoneNumber"],
            MonthlyIncome: dataToSubmit["PersonalInformation-MonthlyIncome"],
        }   
        : {
            FirstName: dataToSubmit["PersonalInformation-FirstName"],
            MiddleInitial: dataToSubmit["PersonalInformation-MiddleInitial"],
            LastName: dataToSubmit["PersonalInformation-LastName"],
            EmailAddress: dataToSubmit["PersonalInformation-EmailAddress"],

            // DPM-13680 Primary Home Address AB Test
            ...(SessionStorage.verifyNotEmpty('primaryHomeABInfo') 
            && dataToSubmit["PersonalInformation-HomeAddressLine1"]
            && dataToSubmit["PersonalInformation-HomeCity"]
            && dataToSubmit["PersonalInformation-HomeState"]
            && dataToSubmit["PersonalInformation-HomeZipCode"]
            ? {
                HomeAddressLine1: dataToSubmit["PersonalInformation-MailingAddressLine1"],
                HomeAddressLine2: dataToSubmit["PersonalInformation-MailingAddressLine2"],
                HomeCity: dataToSubmit["PersonalInformation-MailingCity"],
                HomeState: dataToSubmit["PersonalInformation-MailingState"],
                HomeZipCode: dataToSubmit["PersonalInformation-MailingZipCode"],

                IsMailingHomeAddressSame: dataToSubmit["PersonalInformation-IsMailingHomeAddressSame"],

                MailingAddressLine1: dataToSubmit["PersonalInformation-HomeAddressLine1"],
                MailingAddressLine2: dataToSubmit["PersonalInformation-HomeAddressLine2"],
                MailingCity: dataToSubmit["PersonalInformation-HomeCity"],
                MailingState: dataToSubmit["PersonalInformation-HomeState"],
                MailingZipCode: dataToSubmit["PersonalInformation-HomeZipCode"],
            } : {
                MailingAddressLine1: dataToSubmit["PersonalInformation-MailingAddressLine1"],
                MailingAddressLine2: dataToSubmit["PersonalInformation-MailingAddressLine2"],
                MailingCity: dataToSubmit["PersonalInformation-MailingCity"],
                MailingState: dataToSubmit["PersonalInformation-MailingState"],
                MailingZipCode: dataToSubmit["PersonalInformation-MailingZipCode"],
                
                IsMailingHomeAddressSame: dataToSubmit["PersonalInformation-IsMailingHomeAddressSame"],
    
                HomeAddressLine1: dataToSubmit["PersonalInformation-HomeAddressLine1"],
                HomeAddressLine2: dataToSubmit["PersonalInformation-HomeAddressLine2"],
                HomeCity: dataToSubmit["PersonalInformation-HomeCity"],
                HomeState: dataToSubmit["PersonalInformation-HomeState"],
                HomeZipCode: dataToSubmit["PersonalInformation-HomeZipCode"],
            }),

            PrimaryPhoneNumber: dataToSubmit["PersonalInformation-PrimaryPhoneNumber"],
            MonthlyIncome: dataToSubmit["PersonalInformation-MonthlyIncome"],
            Ssn: dataToSubmit["PersonalInformation-Ssn"],
            DOBMonth: dataToSubmit["PersonalInformation-DOBMonth"],
            DOBDay: dataToSubmit["PersonalInformation-DOBDay"],
            DOBYear: dataToSubmit["PersonalInformation-DOBYear"],
        };

    const fundingInfoObject: Partial<TSubmitDataPqSkipPqPqOfOne> = dataToSubmit["Funding-FundingPaymentSource"] === 1 
        ? {
            FundingInformation: dataToSubmit["Funding-FundingInformation"],
        }
        : {
            DebitFundingInformation: dataToSubmit["Funding-DebitFundingInformation"],
        };

    const securedCardObject: Partial<TSubmitDataPqSkipPqPqOfOne> = 
        !dataToSubmit["IsSecuredCard"] 
            ? undefined 
            : {
                AgreedToFullESign: dataToSubmit["AgreedToFullESign"],
                FundingPaymentSource: dataToSubmit["Funding-FundingPaymentSource"],
                ...fundingInfoObject,
            };

    return ({
        PersonalInformation: {
            ...personalInformationObject,
            isDataEntryPage: false, // Deprecated?
            ioBlackBox: blackBoxData?.Blackbox?.ThirdParty,
            fpBB: blackBoxData?.Blackbox?.FirstParty
        },
        MobilePhoneNumber: {
            Number: '',
            Agree: '',
        },

        AuthorizedUser: {
            IsAuthorizedUserSelected: dataToSubmit["AuthorizedUser-IsAuthorizedUserSelected"],
            IsAuthorizedUserSpouse: dataToSubmit["AuthorizedUser-IsAuthorizedUserSpouse"],
            AuthorizedUserFirstName: dataToSubmit["AuthorizedUser-AuthorizedUserFirstName"],
            AuthorizedUserMiddleInitial: dataToSubmit["AuthorizedUser-AuthorizedUserMiddleInitial"],
            AuthorizedUserLastName: dataToSubmit["AuthorizedUser-AuthorizedUserLastName"],
            AuthorizedUserDOBDay: dataToSubmit["AuthorizedUser-AuthorizedUserDOBDay"],
            AuthorizedUserDOBMonth: dataToSubmit["AuthorizedUser-AuthorizedUserDOBMonth"],
            AuthorizedUserDOBYear: dataToSubmit["AuthorizedUser-AuthorizedUserDOBYear"],
        },

        SelectedCardId: dataToSubmit["SelectedProgramPlasticCode"],

        IsSecuredCard: dataToSubmit["IsSecuredCard"],
        ...securedCardObject,

        ApplicantESignInterest: dataToSubmit["ApplicantESignInterest"],
        ESignDisclosureVersion: dataToSubmit["ESignDisclosureVersion"],

        ConsumerAcknowledgment: dataToSubmit['ConsumerAcknowledgment'],

        IsBalanceTransferSelected: dataToSubmit["IsBalanceTransferSelected"],

        CreditProtectionSelected: dataToSubmit["CreditProtection-CreditProtectionSelected"],
        CreditProtectionInitials: dataToSubmit["CreditProtection-CreditProtectionInitials"],

        AgreedToPrivacyPolicy: dataToSubmit["AgreedToPrivacyPolicy"],
    });
}

export const buildSubmitDataForPA = async (dataToSubmit: TApplicationData): Promise<TSubmitDataPreApproved> => {
    const sessionStorageData = await SessionStorage.tryGetCipher('pre-approval-data', {} as any); // As any used here to surpress typescript errors on below fields until proper pre-approval-data type is created
    const blackBoxData = blackboxUtils.getBlackbox();

    return ({
        Applicant: {
            ApplicantName: {
                FirstName: dataToSubmit["PersonalInformation-FirstName"],
                MiddleInitial: dataToSubmit["PersonalInformation-MiddleInitial"],
                MiddleName: '',
                LastName: dataToSubmit["PersonalInformation-LastName"],
                NameSuffix: dataToSubmit["personalinformation-NameSuffix"]
            },
            SocialSecurityNumber: {
                FullSocialSecurityNumber: dataToSubmit["PersonalInformation-Ssn"],
            },
            MailingAddress: {
                AddressLineOne: dataToSubmit["PersonalInformation-MailingAddressLine1"],
                AddressLineTwo: dataToSubmit["PersonalInformation-MailingAddressLine2"],
                City: dataToSubmit["PersonalInformation-MailingCity"],
                State: dataToSubmit["PersonalInformation-MailingState"],
                ZipCode: dataToSubmit["PersonalInformation-MailingZipCode"],
                ZipPlusFour: sessionStorageData.Applicant.MailingAddress.ZipPlusFour,
                AddressType: sessionStorageData.Applicant.MailingAddress.AddressType,
            },
            HomeAddress: {
                AddressLineOne: dataToSubmit["PersonalInformation-HomeAddressLine1"],
                AddressLineTwo: dataToSubmit["PersonalInformation-HomeAddressLine2"],
                City: dataToSubmit["PersonalInformation-HomeCity"],
                State: dataToSubmit["PersonalInformation-HomeState"],
                ZipCode: dataToSubmit["PersonalInformation-HomeZipCode"],
                ZipPlusFour: sessionStorageData.Applicant.HomeAddress.ZipPlusFour,
                AddressType: sessionStorageData.Applicant.HomeAddress.AddressType,
            },
            PrimaryPhoneNumber: {
                Number: dataToSubmit["PersonalInformation-PrimaryPhoneNumber"],
            },
            SecondaryPhoneNumber: {
                Number: '',
            },
            EmailAddress: {
                Value: dataToSubmit["PersonalInformation-EmailAddress"],
            },
            DateOfBirth: `${
                dataToSubmit["PersonalInformation-DOBMonth"]
            }/${
                dataToSubmit["PersonalInformation-DOBDay"]
            }/${
                dataToSubmit["PersonalInformation-DOBYear"]
            }`,
            MonthlyIncome: dataToSubmit["PersonalInformation-MonthlyIncome"],
            IsMailingAddressSameAsHomeAddress: dataToSubmit["PersonalInformation-IsMailingHomeAddressSame"],
            IsAddressChangedForSolicitation: sessionStorageData.Applicant.IsAddressChangedForSolicitation,
            CreditScore: sessionStorageData.Applicant.CreditScore,
            IsMonthlyIncomeNull: sessionStorageData.Applicant.IsMonthlyIncomeNull,
            MonthlyExpenses: sessionStorageData.Applicant.MonthlyExpenses,
        },

        AuthorizedUser: {
            IsAuthorizedUserSelected: dataToSubmit["AuthorizedUser-IsAuthorizedUserSelected"],
            IsAuthorizedUserSpouse: dataToSubmit["AuthorizedUser-IsAuthorizedUserSpouse"],
            AuthorizedUserFirstName: dataToSubmit["AuthorizedUser-AuthorizedUserFirstName"],
            AuthorizedUserMiddleInitial: dataToSubmit["AuthorizedUser-AuthorizedUserMiddleInitial"],
            AuthorizedUserLastName: dataToSubmit["AuthorizedUser-AuthorizedUserLastName"],
            AuthorizedUserDOBDay: dataToSubmit["AuthorizedUser-AuthorizedUserDOBDay"],
            AuthorizedUserDOBMonth: dataToSubmit["AuthorizedUser-AuthorizedUserDOBMonth"],
            AuthorizedUserDOBYear: dataToSubmit["AuthorizedUser-AuthorizedUserDOBYear"],
        },
        CreditProtection: {
            IsCreditProtectionSelected: dataToSubmit["CreditProtection-CreditProtectionSelected"],
            CreditProtectionInitials: dataToSubmit["CreditProtection-CreditProtectionInitials"],
        },
        IsBalanceTransferSelected: dataToSubmit.IsBalanceTransferSelected,
        ApplicantESignInterest: dataToSubmit.ApplicantESignInterest,
        ESignDisclosureVersion: dataToSubmit.ESignDisclosureVersion,
        ConsumerAcknowledgment: dataToSubmit.ConsumerAcknowledgment,
        SelectedProgramPlasticCode: dataToSubmit.SelectedProgramPlasticCode,

        MarketingAttributes: getMarketingAttributesForAcquisitions(),
        ReservationNumber: sessionStorageData.ReservationNumber,
        SolicitationId: parseInt(sessionStorageData.SolicitationId),

        DeviceInformation: blackBoxData,
        BlackBox: blackBoxData?.Blackbox,
    })
}

export const buildSubmitFlowApplicationData = async ( dataToSubmit: TApplicationData ): Promise<TSubmitDataPHA> => {
    const deviceInfo = blackboxUtils.getBlackbox();

    const flowApplicationData = {
        ...await getProgramData() // Assuming this pulls from applicationOfferResponse? Not cleanly documented
    } as TSubmitDataPHA;

    flowApplicationData.parties[0].identification = {
        type: 'SSN',
        value: dataToSubmit["PersonalInformation-Ssn"],
    };

    flowApplicationData.programCode = dataToSubmit["ProgramCode"];

    flowApplicationData.AuthorizedUser = {
        IsAuthorizedUserSelected: dataToSubmit["AuthorizedUser-IsAuthorizedUserSelected"],
        IsAuthorizedUserSpouse: dataToSubmit["AuthorizedUser-IsAuthorizedUserSpouse"],
        AuthorizedUserFirstName: dataToSubmit["AuthorizedUser-AuthorizedUserFirstName"],
        AuthorizedUserMiddleInitial: dataToSubmit["AuthorizedUser-AuthorizedUserMiddleInitial"],
        AuthorizedUserLastName: dataToSubmit["AuthorizedUser-AuthorizedUserLastName"],
        AuthorizedUserDOBDay: dataToSubmit["AuthorizedUser-AuthorizedUserDOBDay"],
        AuthorizedUserDOBMonth: dataToSubmit["AuthorizedUser-AuthorizedUserDOBMonth"],
        AuthorizedUserDOBYear: dataToSubmit["AuthorizedUser-AuthorizedUserDOBYear"],
    };

    flowApplicationData.creditProtectionSelected = dataToSubmit['CreditProtection-CreditProtectionSelected'];
    flowApplicationData.creditProtectionInitials = dataToSubmit['CreditProtection-CreditProtectionInitials'];

    flowApplicationData.SelectedCardId = dataToSubmit["SelectedProgramPlasticCode"];
    flowApplicationData.SelectedProgramPlasticCode = dataToSubmit["SelectedProgramPlasticCode"];

    flowApplicationData.AgreedToPrivacyPolicy = dataToSubmit['AgreedToPrivacyPolicy'];
    flowApplicationData.IsBalanceTransferSelected = dataToSubmit["IsBalanceTransferSelected"];
    flowApplicationData.DeviceInformation = deviceInfo;
    flowApplicationData.BlackBox = deviceInfo?.Blackbox;

    flowApplicationData.ApplicantESignInterest = dataToSubmit["ApplicantESignInterest"];
    flowApplicationData.AgreedToFullESign = dataToSubmit["AgreedToFullESign"];
    flowApplicationData.ESignDisclosureVersion = dataToSubmit["ESignDisclosureVersion"];
    flowApplicationData.ConsumerAcknowledgment = dataToSubmit["ConsumerAcknowledgment"];
    flowApplicationData.IsMailingHomeAddressSame = dataToSubmit["PersonalInformation-IsMailingHomeAddressSame"]
    flowApplicationData.IsSecuredCard = dataToSubmit["IsSecuredCard"];

    flowApplicationData.MarketingAttributes = getMarketingAttributesForAcquisitions();

    return flowApplicationData;
};
